import { useState } from 'react';
import { FileSignature } from 'lucide-react';
import type { SessionUserDto } from '@shared/contracts/auth';
import { MEMBERSHIP_AGREEMENT_VERSION } from '@shared/content/membershipAgreement';
import { useLogout, useSignAgreement } from '../../features/auth/useAuth';
import { useLocale, useT } from '../../i18n/useT';
import { todayISO } from '../../lib/calendarRange';
import { AgreementText } from '../profile/AgreementText';
import { formatSignatureDay } from '../profile/signatureDate';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { ZeeLogo } from '../ZeeLogo';

/**
 * Üyelik sözleşmesi kapısı.
 *
 * Sunucudaki kilidin (`requireAuth` → 403 AGREEMENT_REQUIRED) kullanıcıya
 * görünen yüzü; şifre rotasyonu ekranının kardeşi ve ondan SONRA gelir.
 * Üye metni okur, adını yazar ve onaylar; ad, tarih ve sürüm sunucuda
 * `AgreementSignature` satırına yazılır. Onay gidince oturum güncellenir ve
 * `agreementRequired` düştüğü için panel açılır.
 *
 * "Sonuna kadar okudu" bir kilit DEĞİL, bir ipucu: ekran okuyucu ya da
 * klavye kullanıcısını kaydırma olayına bağlamak onu dışarıda bırakırdı.
 * Onayın kendisi zorunlu (onay kutusu + ad).
 */

interface AgreementPageProps {
  user: SessionUserDto;
}

const MIN_NAME_LENGTH = 3;
/** Metnin sonuna bu kadar piksel kala "okundu" sayılır. */
const READ_THRESHOLD_PX = 24;

export function AgreementPage({ user }: AgreementPageProps) {
  const t = useT();
  const { locale } = useLocale();
  const signAgreement = useSignAgreement();
  const logout = useLogout();

  const [fullName, setFullName] = useState(user.name);
  const [isAccepted, setIsAccepted] = useState(false);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const nameError = fullName.trim().length < MIN_NAME_LENGTH ? t.agreement.nameError : undefined;
  const canSubmit = nameError === undefined && isAccepted;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      setShowErrors(true);
      return;
    }
    signAgreement.mutate({
      version: MEMBERSHIP_AGREEMENT_VERSION,
      fullName: fullName.trim(),
      accepted: true,
    });
  };

  return (
    <div className="min-h-screen bg-canvas px-4 py-10 sm:py-14" data-testid="agreement-page">
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <div className="flex justify-center">
          <ZeeLogo size="lg" showTextLabel />
        </div>

        <header className="space-y-2 text-center">
          <h1 className="flex items-center justify-center gap-2 font-serif text-display font-semibold text-ink">
            <FileSignature aria-hidden className="h-6 w-6 text-accent" />
            {t.agreement.title}
          </h1>
          <p className="mx-auto max-w-xl text-body text-ink-2">{t.agreement.intro}</p>
          {locale !== 'tr' && t.agreement.turkishOnly !== '' && (
            <p className="mx-auto max-w-xl text-micro text-ink-3">{t.agreement.turkishOnly}</p>
          )}
        </header>

        <div className="rounded-sm border border-line bg-surface shadow-sm">
          <div
            className="max-h-[55vh] overflow-y-auto p-6 sm:p-8"
            tabIndex={0}
            aria-label={t.agreement.title}
            data-testid="agreement-text"
            onScroll={(event) => {
              const target = event.currentTarget;
              if (target.scrollHeight - target.scrollTop - target.clientHeight <= READ_THRESHOLD_PX) {
                setHasReadToEnd(true);
              }
            }}
          >
            <AgreementText />
          </div>
          <p
            className="border-t border-line px-6 py-2 text-micro text-ink-3"
            aria-live="polite"
          >
            {hasReadToEnd ? t.agreement.readDone : t.agreement.readHint}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-sm border border-line bg-surface p-6 shadow-sm sm:p-8"
          noValidate
        >
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <TextField
              label={t.agreement.nameLabel}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              autoComplete="name"
              maxLength={120}
              hint={t.agreement.nameHint}
              error={showErrors ? nameError : undefined}
              data-testid="agreement-name"
            />
            <div>
              <p className="mb-1.5 block text-label font-semibold text-ink-2">{t.agreement.dateLabel}</p>
              <p className="tabular tap-target flex items-center rounded-sharp border border-line bg-sunken px-3 font-medium text-ink">
                {formatSignatureDay(todayISO())}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 text-body text-ink">
            <input
              type="checkbox"
              checked={isAccepted}
              onChange={(event) => setIsAccepted(event.target.checked)}
              className="mt-1 h-4 w-4 rounded-sharp border-line-strong text-accent focus:ring-2 focus:ring-accent/20"
              aria-invalid={showErrors && !isAccepted ? true : undefined}
              data-testid="agreement-accept"
            />
            <span className={showErrors && !isAccepted ? 'font-semibold text-risk' : undefined}>
              {t.agreement.accept}
            </span>
          </label>

          <Button
            type="submit"
            intent="accent"
            size="lg"
            block
            isLoading={signAgreement.isPending}
            data-testid="agreement-submit"
          >
            {t.agreement.submit}
          </Button>

          <div className="flex flex-wrap items-center justify-between gap-2 text-micro text-ink-3">
            <span>{t.agreement.version(MEMBERSHIP_AGREEMENT_VERSION)}</span>
            <button
              type="button"
              onClick={() => logout.mutate()}
              data-testid="agreement-logout"
              className="uppercase tracking-wider underline-offset-4 transition-colors hover:text-ink-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t.agreement.signOut}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
